import RestaurantOccupancyStatsBlock from "./RestaurantOccupancyStatsBlock";
import {Stack} from "@mui/material";
import React from "react";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {
    ReservationDaySlice,
    ReservationStatus,
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {daySlices} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {createDateBasedOnHHMM} from "crqlar-react-common/dist/utils/date.util";
import {sumGuestCountsByAgeCategory} from "crqlar-react-common/dist/utils/reservationGuestCounts.util";
import {useSectorReservations} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import {AgeCategoryType} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";

export interface OccupancyStatsScope {
    sectorId: string;
    resources: Resource[];
    fromDate: Date;
    toDate: Date;
    statuses: ReservationStatus[];
    daySlice?: ReservationDaySlice;
    timelineSpan?: "day" | "week" | "month";
}

interface RestaurantOccupancyStatsProps {
    fullWidth?: boolean;
    scope: OccupancyStatsScope;
}

const RestaurantOccupancyStats = ({
    fullWidth = false,
    scope,
}: RestaurantOccupancyStatsProps) => {
    const {t} = useLocalize()

    const {
        sectorId,
        resources,
        fromDate,
        toDate,
        statuses,
        daySlice,
        timelineSpan,
    } = scope

    const reservationsQuery = useSectorReservations(
        ['reservations', sectorId, fromDate, toDate, statuses, resources.map(r => r.id!)],
        sectorId,
        fromDate,
        toDate,
        statuses,
        resources.map(r => r.id!)
    )

    const reservations = reservationsQuery.data || []

    // This info contains the day period slices (e.g. morning, day, evening)
    // This should also be taken into account when calculating the stats
    const timelineStart = daySlices(daySlice, timelineSpan)[0]
    const timelineEnd = daySlices(daySlice, timelineSpan)[1]

    // We gather all the reservations that fall within the given time period
    const totalReservations = reservations
        ?.filter(r => {
            // Create date objects based on the timeline start/end times (e.g. 00:00, 13:00, 18:00, 23:59)
            const startDate = createDateBasedOnHHMM(timelineStart || '00:00', r.expectedStartDate)
            const endDate = createDateBasedOnHHMM(timelineEnd || '23:59', r.expectedStartDate)

            /*
                If this condition is true ->
                it means we can clearly define whether the reservation is within the given periods:
                - morning: 00:00 - 13:00
                - afternoon: 13:00 - 18:00
                - evening: 18:00 - 24:00
            */
            if((r.expectedStartDate as Date) >= startDate && (r.expectedEndDate as Date) < endDate) {
                return true
            }

            /*
                But what if the reservation spans across multiple periods?
                Then we need to check in which of these periods the reservation spends the most time :)
            */
            const timeSpentInMorningPeriod = Math.min((r.expectedEndDate as Date).getTime(), createDateBasedOnHHMM('13:00', r.expectedStartDate).getTime()) - Math.max((r.expectedStartDate as Date).getTime(), createDateBasedOnHHMM('00:00', r.expectedStartDate).getTime())
            const timeSpentInDayPeriod = Math.min((r.expectedEndDate as Date).getTime(), createDateBasedOnHHMM('18:00', r.expectedStartDate).getTime()) - Math.max((r.expectedStartDate as Date).getTime(), createDateBasedOnHHMM('13:00', r.expectedStartDate).getTime())
            const timeSpentInEveningPeriod = Math.min((r.expectedEndDate as Date).getTime(), createDateBasedOnHHMM('23:59', r.expectedStartDate).getTime()) - Math.max((r.expectedStartDate as Date).getTime(), createDateBasedOnHHMM('18:00', r.expectedStartDate).getTime())

            const maxTimeSpent = Math.max(timeSpentInMorningPeriod, timeSpentInDayPeriod, timeSpentInEveningPeriod)

            if(maxTimeSpent === timeSpentInMorningPeriod && daySlice === 'morning') return true
            if(maxTimeSpent === timeSpentInDayPeriod && daySlice === 'day') return true
            if(maxTimeSpent === timeSpentInEveningPeriod && daySlice === 'evening') return true

            return false
        })

    // Calculating table number stats
    const allResourceIds = totalReservations?.flatMap(r => r.resourceIds) || []
    const allTableIds = useResources(allResourceIds as string[]).filter(r => r.parentId).map(t => t.id) || []

    const activeReservations = totalReservations?.filter(r => r.status === ReservationStatus.ACTIVE) || []
    const completedReservations = totalReservations?.filter(r => r.status === ReservationStatus.COMPLETED) || []
    const pendingReservations = totalReservations?.filter(r => r.status === ReservationStatus.PENDING) || []
    const confirmedReservations = totalReservations?.filter(r => r.status === ReservationStatus.CONFIRMED) || []

    const totalResources = totalReservations?.flatMap(r => r.resourceIds)
    const uniqueTotalTables = Array.from(new Set(totalResources?.filter(r => allTableIds.includes(r)) || []))

    const activeResources = activeReservations.flatMap(r => r.resourceIds)
    const uniqueActiveTables = Array.from(new Set(activeResources.filter(r => allTableIds.includes(r))))

    const completedResources = completedReservations.flatMap(r => r.resourceIds)
    const uniqueCompletedTables = Array.from(new Set(completedResources.filter(r => allTableIds.includes(r))))

    const pendingResources = pendingReservations.flatMap(r => r.resourceIds)
    const uniquePendingTables = Array.from(new Set(pendingResources.filter(r => allTableIds.includes(r))))

    const confirmedResources = confirmedReservations.flatMap(r => r.resourceIds)
    const uniqueConfirmedTables = Array.from(new Set(confirmedResources.filter(r => allTableIds.includes(r))))


    // Calculating the guest counts by age categories
    const allAgeCategories = useAllAgeCategories()

    const adultAgeCategoryIds = allAgeCategories.filter(ac => ac.type === AgeCategoryType.ADULT).map(ac => ac.id)
    const teenagerAgeCategoryIds = allAgeCategories.filter(ac => ac.type === AgeCategoryType.TEENAGER).map(ac => ac.id)
    const childAgeCategoryIds = allAgeCategories.filter(ac => ac.type === AgeCategoryType.CHILD).map(ac => ac.id)
    const infantAgeCategoryIds = allAgeCategories.filter(ac => ac.type === AgeCategoryType.INFANT).map(ac => ac.id)

    const adultCountTotal = sumGuestCountsByAgeCategory(totalReservations, adultAgeCategoryIds as string[])
    const teenagerCountTotal = sumGuestCountsByAgeCategory(totalReservations, teenagerAgeCategoryIds as string[])
    const childCountTotal = sumGuestCountsByAgeCategory(totalReservations, childAgeCategoryIds as string[])
    const infantCountTotal = sumGuestCountsByAgeCategory(totalReservations, infantAgeCategoryIds as string[])
    const sumGuestCountsTotal = adultCountTotal + teenagerCountTotal + childCountTotal + infantCountTotal

    const adultCountPending = sumGuestCountsByAgeCategory(pendingReservations, adultAgeCategoryIds as string[])
    const teenagerCountPending = sumGuestCountsByAgeCategory(pendingReservations, teenagerAgeCategoryIds as string[])
    const childCountPending = sumGuestCountsByAgeCategory(pendingReservations, childAgeCategoryIds as string[])
    const infantCountPending = sumGuestCountsByAgeCategory(pendingReservations, infantAgeCategoryIds as string[])
    const sumGuestCountsPending = adultCountPending + teenagerCountPending + childCountPending + infantCountPending

    const adultCountActive = sumGuestCountsByAgeCategory(activeReservations, adultAgeCategoryIds as string[])
    const teenagerCountActive = sumGuestCountsByAgeCategory(activeReservations, teenagerAgeCategoryIds as string[])
    const childCountActive = sumGuestCountsByAgeCategory(activeReservations, childAgeCategoryIds as string[])
    const infantCountActive = sumGuestCountsByAgeCategory(activeReservations, infantAgeCategoryIds as string[])
    const sumGuestCountsActive = adultCountActive + teenagerCountActive + childCountActive + infantCountActive

    const adultCountCompleted = sumGuestCountsByAgeCategory(completedReservations, adultAgeCategoryIds as string[])
    const teenagerCountCompleted = sumGuestCountsByAgeCategory(completedReservations, teenagerAgeCategoryIds as string[])
    const childCountCompleted = sumGuestCountsByAgeCategory(completedReservations, childAgeCategoryIds as string[])
    const infantCountCompleted = sumGuestCountsByAgeCategory(completedReservations, infantAgeCategoryIds as string[])
    const sumGuestCountsCompleted = adultCountCompleted + teenagerCountCompleted + childCountCompleted + infantCountCompleted

    const adultCountConfirmed = sumGuestCountsByAgeCategory(confirmedReservations, adultAgeCategoryIds as string[])
    const teenagerCountConfirmed = sumGuestCountsByAgeCategory(confirmedReservations, teenagerAgeCategoryIds as string[])
    const childCountConfirmed = sumGuestCountsByAgeCategory(confirmedReservations, childAgeCategoryIds as string[])
    const infantCountConfirmed = sumGuestCountsByAgeCategory(confirmedReservations, infantAgeCategoryIds as string[])
    const sumGuestCountsConfirmed = adultCountConfirmed + teenagerCountConfirmed + childCountConfirmed + infantCountConfirmed

    return(
        <Stack
            direction="column"
            alignItems={fullWidth ? "stretch" : "center"}
            gap={fullWidth ? 1.5 : 2}
            sx={{width: fullWidth ? "100%" : undefined}}
        >

            <RestaurantOccupancyStatsBlock
                statsTitle={t('Pending')}
                tableCount={uniquePendingTables?.length}
                guestCount={sumGuestCountsPending}
                adultCount={adultCountPending}
                teenagerCount={teenagerCountPending}
                childCount={childCountPending}
                infantCount={infantCountPending}
                fullWidth={fullWidth}
            />

            <RestaurantOccupancyStatsBlock
                statsTitle={t('Confirmed')}
                tableCount={uniqueConfirmedTables?.length}
                guestCount={sumGuestCountsConfirmed}
                adultCount={adultCountConfirmed}
                teenagerCount={teenagerCountConfirmed}
                childCount={childCountConfirmed}
                infantCount={infantCountConfirmed}
                fullWidth={fullWidth}
            />

            <RestaurantOccupancyStatsBlock
                statsTitle={t('Active')}
                tableCount={uniqueActiveTables.length}
                guestCount={sumGuestCountsActive}
                adultCount={adultCountActive}
                teenagerCount={teenagerCountActive}
                childCount={childCountActive}
                infantCount={infantCountActive}
                fullWidth={fullWidth}
            />

            <RestaurantOccupancyStatsBlock
                statsTitle={t('Completed')}
                tableCount={uniqueCompletedTables.length}
                guestCount={sumGuestCountsCompleted}
                adultCount={adultCountCompleted}
                teenagerCount={teenagerCountCompleted}
                childCount={childCountCompleted}
                infantCount={infantCountCompleted}
                fullWidth={fullWidth}
            />

        </Stack>
    )
}

export default RestaurantOccupancyStats
